/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment, useEffect} from 'react';
import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';

import RegistrationFields from '@amblique/king-living/app/components/forms/registration-fields';
import {AlertErrorIcon} from '@amblique/king-living/app/components/icons';
import {RecaptchaErrorMessage} from '@amblique/king-living/app/components/recaptcha';
import Link from '@salesforce/retail-react-app/app/components/link';
import {Button, Link as ChakraLink, Stack, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';
import {noop} from '@salesforce/retail-react-app/app/utils/utils';

import WhySignUp from '@amblique/king-living/app/components/why-sign-up';

const RegisterForm = ({
  clickSignIn = noop,
  form,
  prefilledEmail,
  submitForm,
}) => {
  const styles = useMultiStyleConfig('FormAuth', {});

  // Prefill email if provided from login flow
  useEffect(() => {
    if (prefilledEmail) {
      form.setValue('email', prefilledEmail);
    }
  }, [prefilledEmail, form]);

  return (
    <Fragment>
      <Stack className="registration-page-header" {...styles.header}>
        <Text {...styles.title}>
          <FormattedMessage
            defaultMessage="Sign up or login"
            id="login_page.title"
          />
        </Text>
        <Text {...styles.description} {...styles.descriptionBlack}>
          <FormattedMessage
            defaultMessage="Looks like you don't have an account yet. Let's set you up"
            id="login_page.register.description"
          />
        </Text>
      </Stack>
      <form
        data-testid="sf-auth-modal-form-register"
        onSubmit={form.handleSubmit(submitForm)}
      >
        <Stack spacing={{base: 6, lg: 8}}>
          <RecaptchaErrorMessage
            className="form-alert"
            icon={AlertErrorIcon}
            message={form.formState.errors?.global?.message}
          />
          <RegistrationFields form={form} prefilledEmail={prefilledEmail} />
          <Stack {...styles.buttonContainer} {...styles.buttonContainerRegisterForm}>
            <Button
              isLoading={form.formState.isSubmitting}
              type="submit"
              onClick={() => form.clearErrors('global')}
              {...styles.buttonSubmit}
            >
              <FormattedMessage
                defaultMessage="Create account"
                id="register_form.button.create_account"
              />
            </Button>

            <Stack {...styles.policy}>
              <Text {...styles.policyText}>
                <FormattedMessage
                  defaultMessage="By creating an account, you agree to King Living's <policy>Privacy Policy</policy> and <terms>Terms & Conditions</terms>"
                  id="register_form.message.agree_to_policy_terms"
                  values={{
                    policy: (chunks) => (
                      <ChakraLink as={Link} to="/privacy-policy" {...styles.policyLink}>
                        {chunks}
                      </ChakraLink>
                    ),

                    terms: (chunks) => (
                      <ChakraLink
                        as={Link}
                        {...styles.policyLink}
                        to="/terms-conditions"
                      >
                        {chunks}
                      </ChakraLink>
                    ),
                  }}
                />
              </Text>
              <Text {...styles.policyText}>
                <FormattedMessage
                  defaultMessage="This site is protected by reCAPTCHA and the Google <policy>Privacy Policy</policy> and <terms>Terms of Service</terms> apply."
                  id="register_form.google.agree_to_policy_terms"
                  values={{
                    policy: (chunks) => (
                      <ChakraLink href="https://policies.google.com/privacy" {...styles.policyLink}>
                        {chunks}
                      </ChakraLink>
                    ),

                    terms: (chunks) => (
                      <ChakraLink href="https://policies.google.com/terms" {...styles.policyLink}>
                        {chunks}
                      </ChakraLink>
                    ),
                  }}
                />
              </Text>
            </Stack>
          </Stack>
        </Stack>
      </form>
      <WhySignUp />
    </Fragment>
  );
};

RegisterForm.propTypes = {
  clickSignIn: PropTypes.func,
  form: PropTypes.object,
  prefilledEmail: PropTypes.string,
  submitForm: PropTypes.func,
};

export default RegisterForm;
